import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from "react";
import { useApp } from "./AppContext";
import { getSocket } from "../utils/socket";
import { api } from "../utils/api";

type CallType = "voice" | "video";
type Peer = { id: string; name: string; avatar?: string };

type CallState =
  | { status: "idle" }
  | { status: "outgoing"; callId: string; peer: Peer; callType: CallType; conversationId?: string }
  | { status: "incoming"; callId: string; peer: Peer; callType: CallType; conversationId?: string }
  | { status: "connected"; callId: string; peer: Peer; callType: CallType; conversationId?: string; startedAt: number };

type CallContextType = {
  state: CallState;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  muted: boolean;
  startCall: (peer: Peer, conversationId: string, callType: CallType) => void;
  acceptCall: () => void;
  rejectCall: () => void;
  endCall: () => void;
  toggleMute: () => void;
};

const CallContext = createContext<CallContextType | null>(null);
export const useCall = () => {
  const ctx = useContext(CallContext);
  if (!ctx) throw new Error("useCall must be used within CallProvider");
  return ctx;
};

const FALLBACK_ICE_SERVERS = [{ urls: "stun:stun.l.google.com:19302" }, { urls: "stun:stun1.l.google.com:19302" }];

// Serveur TURN self-hosté (identifiants à durée de vie limitée, renouvelés à chaque appel) —
// sans lui, un appel entre deux réseaux restrictifs (4G, wifi d'entreprise...) peut échouer en
// silence malgré STUN. Repli sur STUN seul si l'appel à l'API échoue, plutôt que de bloquer
// l'appel entièrement.
let cachedIceServers: RTCIceServer[] | null = null;
async function getIceServers(): Promise<RTCIceServer[]> {
  if (cachedIceServers) return cachedIceServers;
  try {
    const data = await api.get("/calls/turn-credentials");
    if (Array.isArray(data?.iceServers) && data.iceServers.length > 0) {
      cachedIceServers = data.iceServers;
      return data.iceServers;
    }
  } catch { /* repli STUN ci-dessous */ }
  return FALLBACK_ICE_SERVERS;
}

// Appels vocaux/vidéo intégrés — le serveur ne relaie que la négociation WebRTC (signal),
// le flux audio/vidéo circule directement entre les deux navigateurs. Ni l'un ni l'autre ne
// voit jamais le numéro de téléphone de l'autre.
export function CallProvider({ children }: { children: React.ReactNode }) {
  const { token, user } = useApp();
  const [state, setState] = useState<CallState>({ status: "idle" });
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [muted, setMuted] = useState(false);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const stateRef = useRef<CallState>(state);
  stateRef.current = state;
  // Bug racine des appels cassés : un candidat ICE distant peut arriver (via le signal socket)
  // avant que setRemoteDescription() ait été appelé sur notre PeerConnection — addIceCandidate()
  // lève alors une InvalidStateError, silencieusement avalée par le catch plus bas. Ça n'empêche
  // pas TOUJOURS l'appel de passer (WebRTC retente d'autres paires de candidats), mais sur un
  // réseau qui a besoin de son candidat TURN/relay tôt (4G, wifi restrictif), perdre justement
  // celui-là empêche l'ICE de jamais trouver de paire viable — l'appel semble "sonner dans le
  // vide" ou rester bloqué en connexion. On met en file les candidats reçus trop tôt et on les
  // rejoue juste après setRemoteDescription, au lieu de les jeter.
  const pendingCandidatesRef = useRef<RTCIceCandidateInit[]>([]);

  const cleanup = useCallback(() => {
    pcRef.current?.close();
    pcRef.current = null;
    pendingCandidatesRef.current = [];
    localStream?.getTracks().forEach(t => t.stop());
    setLocalStream(null);
    setRemoteStream(null);
    setMuted(false);
    setState({ status: "idle" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localStream]);

  const createPeerConnection = useCallback(async (peerId: string, callId: string) => {
    const iceServers = await getIceServers();
    const pc = new RTCPeerConnection({ iceServers });
    pendingCandidatesRef.current = [];
    pc.onicecandidate = (e) => {
      if (e.candidate) {
        getSocket(token!).emit("call:signal", { toUserId: peerId, callId, signal: { kind: "candidate", candidate: e.candidate } });
      }
    };
    pc.ontrack = (e) => {
      setRemoteStream(e.streams[0]);
    };
    // Tentative de reprise automatique si l'ICE se déconnecte en cours d'appel (perte réseau
    // momentanée, changement wifi/4G) — sans ça, une coupure réseau de quelques secondes
    // terminait l'appel pour de bon même quand la connexion revenait juste après.
    pc.oniceconnectionstatechange = () => {
      if (pc.iceConnectionState === "failed") {
        pc.restartIce();
      }
    };
    pcRef.current = pc;
    return pc;
  }, [token]);

  // Applique les candidats reçus avant que la description distante ne soit prête, dans l'ordre
  // d'arrivée, puis vide la file — à appeler juste après chaque setRemoteDescription réussi.
  const flushPendingCandidates = useCallback(async (pc: RTCPeerConnection) => {
    const queued = pendingCandidatesRef.current;
    pendingCandidatesRef.current = [];
    for (const c of queued) {
      try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch (error) { console.error("Candidat ICE en file, erreur:", error); }
    }
  }, []);

  const startCall = useCallback((peer: Peer, conversationId: string, callType: CallType) => {
    if (!token) return;
    const socket = getSocket(token);
    const onRinging = ({ callId }: { callId: string }) => {
      setState({ status: "outgoing", callId, peer, callType, conversationId });
    };
    socket.once("call:ringing", onRinging);
    socket.emit("call:invite", { toUserId: peer.id, conversationId, callType });
  }, [token]);

  const acceptCall = useCallback(async () => {
    const s = stateRef.current;
    if (s.status !== "incoming" || !token) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: s.callType === "video" });
      setLocalStream(stream);
      const pc = await createPeerConnection(s.peer.id, s.callId);
      stream.getTracks().forEach(track => pc.addTrack(track, stream));
      setState({ status: "connected", callId: s.callId, peer: s.peer, callType: s.callType, conversationId: s.conversationId, startedAt: Date.now() });
      getSocket(token).emit("call:accept", { callId: s.callId, toUserId: s.peer.id });
    } catch {
      alert("Impossible d'accéder au micro/caméra");
      rejectCall();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, createPeerConnection]);

  const rejectCall = useCallback(() => {
    const s = stateRef.current;
    if (s.status !== "incoming" || !token) return;
    getSocket(token).emit("call:reject", { callId: s.callId, toUserId: s.peer.id, conversationId: s.conversationId, callType: s.callType });
    setState({ status: "idle" });
  }, [token]);

  const endCall = useCallback(() => {
    const s = stateRef.current;
    if (s.status === "idle" || !token) return;
    const durationSeconds = s.status === "connected" ? Math.round((Date.now() - s.startedAt) / 1000) : 0;
    getSocket(token).emit("call:end", { toUserId: s.peer.id, callId: s.callId, conversationId: s.conversationId, callType: s.callType, durationSeconds, answered: s.status === "connected" });
    cleanup();
  }, [token, cleanup]);

  const toggleMute = useCallback(() => {
    const nextMuted = !muted;
    localStream?.getAudioTracks().forEach(t => { t.enabled = !nextMuted; });
    setMuted(nextMuted);
  }, [localStream, muted]);

  useEffect(() => {
    if (!token || !user) return;
    const socket = getSocket(token);

    const onIncoming = (data: { callId: string; conversationId: string; callType: CallType; fromUserId: string; fromUserName: string; fromUserAvatar?: string }) => {
      if (stateRef.current.status !== "idle") {
        // Déjà en communication : on ne peut pas gérer un second appel en parallèle.
        socket.emit("call:reject", { callId: data.callId, toUserId: data.fromUserId, conversationId: data.conversationId, callType: data.callType });
        return;
      }
      setState({ status: "incoming", callId: data.callId, peer: { id: data.fromUserId, name: data.fromUserName, avatar: data.fromUserAvatar }, callType: data.callType, conversationId: data.conversationId });
    };

    const onAccepted = async ({ callId }: { callId: string }) => {
      const s = stateRef.current;
      if (s.status !== "outgoing" || s.callId !== callId) return;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: s.callType === "video" });
        setLocalStream(stream);
        const pc = await createPeerConnection(s.peer.id, s.callId);
        stream.getTracks().forEach(track => pc.addTrack(track, stream));
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        socket.emit("call:signal", { toUserId: s.peer.id, callId: s.callId, signal: { kind: "offer", sdp: offer } });
        setState({ status: "connected", callId: s.callId, peer: s.peer, callType: s.callType, conversationId: s.conversationId, startedAt: Date.now() });
      } catch {
        alert("Impossible d'accéder au micro/caméra");
        socket.emit("call:end", { toUserId: s.peer.id, callId: s.callId, answered: false });
        cleanup();
      }
    };

    const onRejected = () => {
      if (stateRef.current.status === "outgoing") cleanup();
    };

    const onSignal = async ({ callId, signal }: { callId: string; fromUserId: string; signal: any }) => {
      const s = stateRef.current;
      if (s.status !== "connected" || s.callId !== callId) return;
      let pc = pcRef.current;
      try {
        if (signal.kind === "offer") {
          if (!pc) return;
          await pc.setRemoteDescription(new RTCSessionDescription(signal.sdp));
          await flushPendingCandidates(pc);
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          socket.emit("call:signal", { toUserId: s.peer.id, callId, signal: { kind: "answer", sdp: answer } });
        } else if (signal.kind === "answer") {
          if (!pc) return;
          await pc.setRemoteDescription(new RTCSessionDescription(signal.sdp));
          await flushPendingCandidates(pc);
        } else if (signal.kind === "candidate") {
          // pc peut exister sans remoteDescription encore posée (offre/réponse pas encore
          // traitée) : addIceCandidate lèverait alors une erreur — on met en file dans les deux
          // cas (pc absent OU remoteDescription pas encore prête) plutôt que de perdre le
          // candidat.
          if (pc && pc.remoteDescription) {
            await pc.addIceCandidate(new RTCIceCandidate(signal.candidate));
          } else {
            pendingCandidatesRef.current.push(signal.candidate);
          }
        }
      } catch (error) {
        console.error("WebRTC signal error:", error);
      }
    };

    const onEnded = () => {
      if (stateRef.current.status !== "idle") cleanup();
    };

    socket.on("call:incoming", onIncoming);
    socket.on("call:accepted", onAccepted);
    socket.on("call:rejected", onRejected);
    socket.on("call:signal", onSignal);
    socket.on("call:ended", onEnded);
    return () => {
      socket.off("call:incoming", onIncoming);
      socket.off("call:accepted", onAccepted);
      socket.off("call:rejected", onRejected);
      socket.off("call:signal", onSignal);
      socket.off("call:ended", onEnded);
    };
  }, [token, user, createPeerConnection, cleanup]);

  return (
    <CallContext.Provider value={{ state, localStream, remoteStream, muted, startCall, acceptCall, rejectCall, endCall, toggleMute }}>
      {children}
    </CallContext.Provider>
  );
}
